import { QUERY_STRING_EXTERNAL_USER_ID } from "@/constants";
import {
  ReduxState,
  PersonalInfoViewModel,
  Metadata,
  VerificationResponse,
  VerificationServiceAction,
  ViewModel,
  MetadataConfig,
  ProgramTheme,
  FieldId,
} from "../types/types";
import { getFieldValidationErrorsEmpty } from "../types/empties";
import { VerificationStepsEnum, isCollectStep } from "../types/runtimeTypes";
import { setMetadata, getMetadata } from "../metadata/metadata";
import { deepClone, deepMerge } from "../utils/objects";
import {
  recordVerificationResponse,
  setUserDimension,
  isTestValues,
  CustomDimensionNames,
} from "../GoogleAnalytics/ga";
import { getMetadataConfig } from "../ProgramTheme/programThemeGetters";
import { getQueryOrOption, isMockedStep } from "../ServerApi/TestingRouteOverrides";

const allFields = [
  "activeDutyStartDate",
  "address1",
  "birthDate",
  "city",
  "country",
  "cvecNumber",
  "dischargeDate",
  "driverLicenseNumber",
  "ebtCardNumber",
  "email",
  "firstName",
  "lastName",
  "marketConsentValue",
  "memberId",
  "phoneNumber",
  "postalCode",
  "socialSecurityNumber",
  "state",
  "status",
  "statuses",
];

/**
 * The initial state of the verification service.
 */
const initialState: ReduxState = Object.freeze({
  programId: undefined,
  programTheme: undefined,
  isLoading: true,
  isErrored: false,
  viewModel: undefined,
  previousViewModel: undefined,
  previousVerificationResponse: undefined,
  verificationResponse: undefined,
  fieldValidationErrors: getFieldValidationErrorsEmpty(),
  messages: undefined,
  orgList: undefined,
  formValidationOptions: {},
});

const getInitialState = () => deepClone(initialState);

/**
 * @description Most of the state for the VerificationService
 * @example as a user fills-in the HTML form, we can track the data entered here in the view model.
 * @param state
 * @param action
 */
export const verificationServiceReducer = (
  state: ReduxState = getInitialState(),
  action: VerificationServiceAction,
): ReduxState => {
  switch (action.type) {
    case "PROGRAM_ID":
      setUserDimension(CustomDimensionNames.programId, action.programId);
      return {
        ...state,
        programId: action.programId,
      };
    case "VERIFICATION_RESPONSE":
      let previousVerificationResponse: VerificationResponse;
      if (
        state.verificationResponse &&
        (state.verificationResponse.currentStep === VerificationStepsEnum.collectMilitaryStatus ||
          state.verificationResponse.currentStep === VerificationStepsEnum.sso ||
          state.verificationResponse.currentStep === VerificationStepsEnum.docUpload)
      ) {
        previousVerificationResponse = state.verificationResponse;
      } else if (state.previousVerificationResponse) {
        previousVerificationResponse = state.previousVerificationResponse;
      } else {
        previousVerificationResponse = state.verificationResponse;
      }

      recordVerificationResponse(action.verificationResponse);
      return {
        ...state,
        previousVerificationResponse,
        // When we get a verificationResponse, hold on to the last viewModel
        // before re-render, in case it needs to be referenced in the new step:
        previousViewModel: state.viewModel,
        verificationResponse: action.verificationResponse,
      };
    case "SET_MESSAGES":
      return {
        ...state,
        messages: action.messages,
      };
    case "FIELD_VALIDATION_ERRORS":
      return {
        ...state,
        fieldValidationErrors: action.fieldValidationErrors,
      };
    case "VIEW_MODEL":
      const newState = { ...state } satisfies ReduxState;

      if (action.viewModel) {
        if (action.partial) {
          // New functionality: action.viewModel can be a sparse object. Bring along all previous viewModel
          // This allows devs to set just one field, like firstName, without supplying the rest of the viewModel object
          newState.viewModel = deepMerge({}, state.viewModel, action.viewModel) as ViewModel;
        } else {
          // Existing functionality: replace viewModel object. Leave here for backwards compatibility.
          // WARNING - this is not a deep clone!
          newState.viewModel = { ...action.viewModel } as ViewModel;
        }
      }

      if (action.viewModel && action.viewModel.metadata) {
        setMetadata(action.viewModel.metadata);
        const metadata: Metadata = getMetadata();
        (newState.viewModel as PersonalInfoViewModel).metadata = metadata;
      }

      return newState;
    case "PREVIOUS_VIEW_MODEL":
      return {
        ...state,
        previousViewModel: action.previousViewModel,
      };
    case "PROGRAM_THEME":
      setUserDimension(
        CustomDimensionNames.testMode,
        action.programTheme.isTestMode ? isTestValues.TRUE : isTestValues.FALSE,
      );

      if (action.partial) {
        return {
          ...state,
          programTheme: deepMerge({}, state.programTheme, action.programTheme) as ProgramTheme,
        };
      }

      return {
        ...state,
        programTheme: action.programTheme,
      };
    case "FORM_VALIDATION_OPTIONS":
      return {
        ...state,
        formValidationOptions: {
          maxAge: state.verificationResponse.maxAge || state.programTheme?.config?.maxAge,
          minAge: state.verificationResponse.minAge || state.programTheme?.config?.minAge,
          smsLoopEnabled: state.programTheme.smsLoopEnabled,
          strictMilitaryValidationEnabled: state.programTheme.strictMilitaryValidationEnabled,
          currentStep: state.verificationResponse.currentStep,
          viewModel: state.viewModel,
        },
      };

    case "REQUIRED_METADATA_AND_EXTERNAL_ID_CHECK": {
      if (!isCollectStep(state.verificationResponse.currentStep) || isMockedStep()) {
        return state;
      }

      const metadataConfig: MetadataConfig = getMetadataConfig(state.programTheme);
      const metadata: Metadata = getMetadata();
      const isExternalUserIdRequired = state.programTheme.externalUserIdRequired;
      const hasMissingMetadata =
        metadataConfig.enabled &&
        !(metadataConfig.requiredKeys || []).every((k) => metadata.hasOwnProperty(k));

      const isMissingExternalId =
        isExternalUserIdRequired && !getQueryOrOption(QUERY_STRING_EXTERNAL_USER_ID);

      const getOverrideStep = () => {
        if (hasMissingMetadata) {
          return "missingRequiredMetadata";
        }
        if (isMissingExternalId) {
          return "missingRequiredExternalId";
        }
        return undefined;
      };

      const overrideStep = getOverrideStep();

      return {
        ...state,
        overrideStep,
      };
    }

    case "IS_LOADING":
      return {
        ...state,
        isLoading: action.isLoading,
        loadingStep: action.isLoading ? action.loadingStep : undefined,
      };
    case "IS_ERRORED":
      return {
        ...state,
        isErrored: action.isErrored,
      };
    case "RESET_STATE":
      return {
        ...deepClone(getInitialState()),
        programId: state.programId,
      };
    case "COLLECT_FIELDS": {
      const requiredFieldKeys = action.requiredFields.map((field) => field.key);

      // if smsloop is enabled or country is "US", add phoneNumber to requiredFieldKeys
      if (
        (state.programTheme.smsLoopEnabled ||
          // @ts-ignore - initially it's country not countryChoice
          state.viewModel?.country === "US" ||
          state.viewModel.countryChoice?.value === "US") && // use countryChoice if available
        !requiredFieldKeys.includes("phoneNumber")
      ) {
        requiredFieldKeys.push("phoneNumber");
      }

      const fieldsToSkip = allFields.filter(
        (fieldId) =>
          allFields.includes(fieldId as FieldId) && !requiredFieldKeys.includes(fieldId as FieldId),
      );

      return {
        ...state,
        viewModel: {
          ...state.viewModel,
          fieldsToSkipValidation: fieldsToSkip as FieldId[],
        } as ViewModel,
      };
    }
    case "FORCE_UPDATE":
      return deepClone(state);
    default:
      return state;
  }
};
